import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { useScrollToTop, scrollToTop } from "@/hooks/useScrollToTop";
import { works } from "@/mocks/works";
import { SITE } from "@/config/site";

export default function Work() {
  useScrollToTop();
  const { id } = useParams();
  const navigate = useNavigate();

  const work = works.find((w) => w.id === id) || works[0];
  const related = works.filter((w) => w.categoryId === work.categoryId && w.id !== work.id).slice(0, 4);
  const similar = related.length > 0 ? related : works.filter((w) => w.id !== work.id).slice(0, 4);

  const episodeList = Array.from({ length: Math.min(work.episodes, 12) }, (_, i) => i + 1);

  const workJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: `${work.title} - 抖漫`,
      url: `${SITE.baseUrl}/work/${work.id}`,
      description: work.desc,
      genre: work.categoryName,
      image: work.cover,
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.baseUrl },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: work.rating,
        bestRating: 10,
        ratingCount: 12800,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE.baseUrl },
        {
          "@type": "ListItem",
          position: 2,
          name: work.categoryName,
          item: `${SITE.baseUrl}/category/${work.categoryId}`,
        },
        { "@type": "ListItem", position: 3, name: work.title, item: `${SITE.baseUrl}/work/${work.id}` },
      ],
    },
  ];

  useSeo({
    title: `${work.title}动态漫画在线看_抖漫${work.categoryName} - 抖漫`,
    description: `${work.title}是抖漫平台上的一部${work.categoryName}动态漫画，竖屏全屏有声特效沉浸观看，已更新至${work.episodes}集，热门作品持续更新，追更不迷路。${work.desc}`,
    keywords: `${work.title},抖漫${work.categoryName},${work.categoryName}动态漫画`,
    path: `/work/${work.id}`,
    jsonLd: workJsonLd,
  });

  const handlePlay = () => {
    navigate(`/category/${work.categoryId}`);
    scrollToTop();
  };

  return (
    <Layout>
      <section className="pt-28 pb-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: work.categoryName, to: `/category/${work.categoryId}` },
                { label: work.title },
              ]}
            />
          </Reveal>

          <Reveal delay={80}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cover */}
              <div className="lg:col-span-1">
                <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                  <img
                    src={work.cover}
                    alt={`${work.title} 动态漫画封面`}
                    title={`${work.title} 抖漫在线看`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div
                    className={`absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs font-medium ${
                      work.status === "已完结"
                        ? "bg-secondary-500 text-background-950"
                        : "bg-primary-500 text-background-950"
                    }`}
                  >
                    {work.status}
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="lg:col-span-2">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-50 mb-3">{work.title}</h1>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded-md bg-background-800 text-primary-400 text-sm whitespace-nowrap">
                    {work.categoryName}
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-background-800 text-foreground-300 text-sm whitespace-nowrap">
                    {work.tag}
                  </span>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">更新：{work.updateAt}</span>
                </div>

                {/* stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="rounded-lg bg-background-900/70 border border-background-200/20 p-3 text-center">
                    <div className="text-xl font-bold text-primary-400">{work.episodes}</div>
                    <div className="text-xs text-foreground-600 mt-1">总集数</div>
                  </div>
                  <div className="rounded-lg bg-background-900/70 border border-background-200/20 p-3 text-center">
                    <div className="text-xl font-bold text-foreground-100">{work.views}</div>
                    <div className="text-xs text-foreground-600 mt-1">播放量</div>
                  </div>
                  <div className="rounded-lg bg-background-900/70 border border-background-200/20 p-3 text-center">
                    <div className="text-xl font-bold text-accent-400">{work.rating.toFixed(1)}</div>
                    <div className="text-xs text-foreground-600 mt-1">评分</div>
                  </div>
                  <div className="rounded-lg bg-background-900/70 border border-background-200/20 p-3 text-center">
                    <div className="text-xl font-bold text-foreground-100">{work.likes}</div>
                    <div className="text-xs text-foreground-600 mt-1">收藏</div>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-foreground-100 font-medium mb-2">剧情简介</h2>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed">{work.desc}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handlePlay}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-950 text-base font-medium whitespace-nowrap hover:bg-primary-400 transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-xl"></i>
                    立即观看
                  </button>
                  <button
                    onClick={scrollToTop}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-background-800 border border-background-300/40 text-foreground-100 text-base font-medium whitespace-nowrap hover:bg-background-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-xl"></i>
                    加入追更
                  </button>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Episode list */}
          <Reveal delay={120}>
            <div className="mt-12">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-50 mb-6">剧集列表</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {episodeList.map((ep) => (
                  <button
                    key={ep}
                    onClick={handlePlay}
                    className="px-3 py-2.5 rounded-lg bg-background-900/70 border border-background-200/20 text-sm text-foreground-300 hover:text-background-950 hover:bg-primary-500 hover:border-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    第{ep}集
                  </button>
                ))}
              </div>
              {work.episodes > 12 && (
                <p className="mt-4 text-xs text-foreground-600">
                  共 {work.episodes} 集，更多剧集点开即看
                </p>
              )}
            </div>
          </Reveal>

          {/* Related */}
          <div className="mt-14 pt-8 border-t border-background-200/20">
            <Reveal>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-50 mb-6">同类推荐</h2>
            </Reveal>
            <Reveal delay={120}>
              <div className="grid grid-cols-2 gap-4 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
                {similar.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </Layout>
  );
}